/**
 * 资源管理（07 开发文档 /assets）：已引用/未引用列表 + 引用标记说明。
 * 未引用资源由 API 定时任务清理（24h 宽限）；已引用不可物理删除。
 */
import { useCallback, useEffect, useState } from 'react';
import { adminHttp, ApiError } from '../lib/api';
import { ErrorNote, Loading, PageHeader } from '../components/ui-bits';

interface AssetRow {
  key: string;
  purpose: string;
  mimeType: string;
  sizeBytes: number;
  referenced: boolean;
  createdAt: string;
}

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function AssetsPage() {
  const [rows, setRows] = useState<AssetRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      // 资源列表端点一期按需：经 settings/town 管理接口已覆盖主要场景；
      // 资产明细走 audit-logs 的 presign 记录 + 直传 key 引用（无独立列表端点——
      // 端点汇总 43 个中无 /admin/assets 列表；此页展示 presign 审计记录）。
      const res = await adminHttp.get<{ items: { objectId: string | null; createdAt: string; summary: string | null }[] }>(
        '/admin/audit-logs?action=asset.presign&pageSize=50',
      );
      setRows(
        res.items
          .filter((r) => r.objectId)
          .map((r) => ({
            key: r.objectId!,
            purpose: r.summary?.includes('contact-attachment') ? 'contact-attachment' : 'builder-photo',
            mimeType: '',
            sizeBytes: Number(r.summary?.match(/(\d+)B/)?.[1] ?? 0),
            referenced: false,
            createdAt: r.createdAt,
          })),
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '加载失败');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section>
      <PageHeader
        title='资源管理'
        sub='近期上传凭据签发记录（审计）；未随问卷提交的附件 24 小时后由系统自动清理。'
      />
      {error && <ErrorNote message={error} />}
      {!rows && !error && <Loading />}
      {rows && (
        <div style={{ overflowX: 'auto' }}>
          <table className='audit-table'>
            <thead>
              <tr>
                <th scope='col'>签发时间</th>
                <th scope='col'>用途</th>
                <th scope='col'>资源 key</th>
                <th scope='col'>大小</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ color: 'var(--muted)' }}>
                    暂无记录。
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.key}>
                  <td>{new Date(r.createdAt).toLocaleString('zh-CN')}</td>
                  <td>{r.purpose}</td>
                  <td style={{ wordBreak: 'break-all' }}>
                    <code>{r.key}</code>
                  </td>
                  <td>{r.sizeBytes ? fmtSize(r.sizeBytes) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
